import { useState } from "react";

import type { Layer } from "../../types/projects";

const HINT_PLACEHOLDERS = [
  "This is actually an inverter datasheet, not a module",
  "Look at the second page for the model number",
  "This is from Trina Solar, not JinkoSolar",
  "This is a tracker spec sheet",
  "Ignore the cover page header",
];

interface Props {
  layer: Layer;
  onCancel: () => void;
  onSubmit: (hint: string) => void;
  submitting?: boolean;
}

/**
 * Stage 8.5: chat-style hint modal for "Reclassify with hints."
 *
 * Compact dialog with one optional textarea. The placeholder text
 * rotates between several example hints so the user sees what the
 * field is for without us having to add scaffolding copy. Submitting
 * empty hint behaves identically to a system-failure retry (cold
 * re-run) — both backend code paths converge.
 */
export function ReclassifyHintModal({
  layer,
  onCancel,
  onSubmit,
  submitting = false,
}: Props) {
  const [hint, setHint] = useState("");
  // Deterministic placeholder pick — refreshes per mount but is
  // stable for a single instance so the placeholder doesn't dance
  // while the user is reading it.
  const [placeholder] = useState(
    () => HINT_PLACEHOLDERS[Math.floor(Math.random() * HINT_PLACEHOLDERS.length)],
  );

  const filename = layer.original_filename || layer.name;
  const klass = layer.classification ?? "unknown";
  const confPct =
    layer.classification_confidence !== null
      ? `${Math.round(layer.classification_confidence * 100)}%`
      : "—";

  return (
    <div
      role="dialog"
      aria-modal="true"
      data-testid="reclassify-hint-modal"
      style={backdropStyle}
    >
      <div style={cardStyle}>
        <h3 style={{ margin: 0, fontSize: "1.05rem" }}>
          Reclassify <span style={{ color: "#475569" }}>{filename}</span>
        </h3>
        <p
          data-testid="reclassify-hint-current"
          style={{ marginTop: "0.5rem", color: "#475569", fontSize: "0.85rem" }}
        >
          Current classification: <strong>{klass}</strong> · confidence {confPct}
        </p>
        <label
          style={{
            display: "block",
            marginTop: "1rem",
            fontSize: "0.85rem",
            color: "#0a0e27",
          }}
        >
          Give me a hint (optional):
        </label>
        <textarea
          data-testid="reclassify-hint-textarea"
          value={hint}
          onChange={(e) => setHint(e.target.value)}
          rows={3}
          placeholder={placeholder}
          maxLength={600}
          style={textareaStyle}
        />
        <div
          style={{
            display: "flex",
            gap: "0.5rem",
            justifyContent: "flex-end",
            marginTop: "1rem",
          }}
        >
          <button
            type="button"
            onClick={onCancel}
            data-testid="reclassify-hint-cancel"
            disabled={submitting}
            style={secondaryBtn}
          >
            Cancel
          </button>
          <button
            type="button"
            onClick={() => onSubmit(hint.trim())}
            data-testid="reclassify-hint-submit"
            disabled={submitting}
            style={primaryBtn}
          >
            {submitting ? "Reanalyzing…" : "Reanalyze"}
          </button>
        </div>
      </div>
    </div>
  );
}

const backdropStyle: React.CSSProperties = {
  position: "fixed",
  inset: 0,
  background: "rgba(0,0,0,0.4)",
  display: "grid",
  placeItems: "center",
  zIndex: 60,
};

const cardStyle: React.CSSProperties = {
  background: "#ffffff",
  borderRadius: 10,
  padding: "1.5rem",
  width: "min(520px, 95vw)",
  boxShadow: "0 20px 40px rgba(0,0,0,0.15)",
};

const textareaStyle: React.CSSProperties = {
  width: "100%",
  marginTop: "0.5rem",
  padding: "0.5rem 0.7rem",
  border: "1px solid #d1d5db",
  borderRadius: 6,
  fontFamily: "inherit",
  fontSize: "0.9rem",
  resize: "vertical",
};

const primaryBtn: React.CSSProperties = {
  background: "#635bff",
  color: "#ffffff",
  border: "none",
  padding: "0.55rem 1rem",
  borderRadius: 6,
  cursor: "pointer",
  fontWeight: 500,
};

const secondaryBtn: React.CSSProperties = {
  background: "#ffffff",
  color: "#0a0e27",
  border: "1px solid #d1d5db",
  padding: "0.55rem 1rem",
  borderRadius: 6,
  cursor: "pointer",
};
